import {NgIf} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import {
  Subject,
  takeUntil,
  throwError as observableThrowError,
  filter,
  interval,
} from 'rxjs';
import {User} from './features/user/shared/user.model';
import EventBusEvent, {
  EventBCType,
  EventBusService,
  EventBusType,
} from './shared/services/event-bus.service';
import {AuthService} from './features/auth/shared/services/auth.service';
import {AlertId, AlertService} from './shared/services/alert.service';
import {AppConfig} from './configs/app.config';
import {authRoutes} from './features/auth/shared/auth-routes';
import {AuthRepository} from './features/auth/store/auth.repository';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, NgIf]
})
export class AppComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();

  user: User | undefined;
  isLoggedIn: boolean | undefined;
  isArrivalRoute: boolean;
  isLoggingOut: boolean;
  window: Window;
  public isUpdating = false;

  // eslint-disable-next-line max-params
  constructor(
    private readonly eventBusService: EventBusService,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly authRepository: AuthRepository,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly document: Document,
    @Inject(LOCALE_ID) public locale: string,
    private readonly swUpdate: SwUpdate
  ) {
    this.isLoggingOut = false;
    this.isArrivalRoute = false;
    this.window = this.document.defaultView as Window;
  }

  async ngOnInit() {
    this.checkAccessToken();
    this.subscribeForEvents();
    this.loadUserInfo();
    this.validateChanges();
  }

  private validateChanges(): void {
    if (this.swUpdate.isEnabled === false) {
      console.table('Actualizaciones automáticas desactivadas');
      return;
    }

    // 1. Validar cambios AL ABRIR la PWA
    this.checkForUpdates();

    // 2. Validar cambios CADA 12 HORAS
    const hours = 12;
    const twelveHoursInMs = hours * 60 * 60 * 1000;
    interval(twelveHoursInMs).subscribe(() => {
      this.checkForUpdates();
    });

    // 3. Suscribirse al evento de "Versión Lista"
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(() => {
        // Bloqueamos la UI y recargamos
        this.applyUpdate();
      });
  }

  private checkForUpdates() {
    // Esto le pregunta al servidor si el archivo ngsw.json ha cambiado
    this.swUpdate.checkForUpdate();
  }

  private applyUpdate() {
    this.isUpdating = true; // Activamos el mensaje en la UI
    console.table('Actualizando App...');
    // Esperamos un segundo para que el usuario alcance a ver el mensaje
    const MLS_1500 = 1500;
    setTimeout(() => {
      // Forzamos la recarga de la página para activar la nueva versión
      document.location.reload();
    }, MLS_1500);
  }

  loadUserInfo() {
    this.authRepository
      .isLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
      });

    this.authRepository.$user.pipe(takeUntil(this.destroy$)).subscribe(user => {
      if (user) {
        this.user = user;
      }
    });
  }

  subscribeForEvents() {
    this.eventBusService.eventsBC.onmessage = event => {
      if (event.data.type === EventBCType.SESSION_CHANGED) {
        this.window.location.reload();
      }
    };

    this.eventBusService.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.type === EventBusType.FINISH_LOGOUT) {
          this.closeSessionAndReload(event);
        }
      });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.handleRouteEvent(event);
    });

    this.window.onfocus = () => {
      this.checkAccessToken();
    };
  }

  handleRouteEvent(event: Event) {
    if (event instanceof NavigationEnd) {
      const accessToken = this.authRepository.getAccessTokenValue();
      if (accessToken) {
        const accessTokenValue = AuthService.decodeToken(accessToken);
        const isAccessTokenExpired =
          Date.now() >= (accessTokenValue?.exp ?? 0) * 1000;
        if (isAccessTokenExpired && !event.url.includes(authRoutes.logout)) {
          this.router.navigate([authRoutes.logout], {
            queryParams: {
              origin: encodeURIComponent(this.window.location.href),
              alertId: AlertId.SESSION_EXPIRED,
            },
          });
        }
      }

      const alertId =
        this.activatedRoute.snapshot.queryParams[
          AppConfig.customQueryParams.alertId
        ];
      if (alertId) {
        this.alertService.create(alertId);
      }

      this.isArrivalRoute = [`/${this.locale}` + authRoutes.logIn].includes(
        this.router.url
      );
    }
  }

  closeSessionAndReload(event: EventBusEvent) {
    this.isLoggingOut = true;
    this.changeDetectorRef.detectChanges();
    this.eventBusService.eventsBC.postMessage({
      type: EventBCType.SESSION_CHANGED,
    });
    this.window.location.href = (event.data as {path: string}).path;
  }

  checkAccessToken() {
    const accessToken = this.authRepository.getAccessTokenValue();

    if (accessToken) {
      const accessTokenValue = AuthService.decodeToken(accessToken);
      const isAccessTokenExpired =
        Date.now() >= (accessTokenValue?.exp ?? 0) * 1000;
      if (isAccessTokenExpired) {
        this.navigateToLogout();
        return observableThrowError(() => new Error());
      }
      return true;
    }
    return true;
  }

  navigateToLogout() {
    this.router.navigate([authRoutes.logout], {
      queryParams: {
        origin: encodeURIComponent(this.window.location.href),
        alertId: AlertId.SESSION_EXPIRED,
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
